import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { listAnimation } from '@animations/list';
import { skeletonEnter } from '@animations/skeleton-enter';
import { staggerAnimation } from '@animations/stagger';
import { Company, DurationEnum, Job } from '@ats/models';
import { EventTrackerService } from '@commons/tracker/event-tracker/event-tracker.service';
import { Select, Store } from '@ngxs/store';
import {
  InitStaticData as InitApplicationStaticData,
  SetTmpApplication,
} from '@store/applications/applications.actions';
import { CompaniesState } from '@store/companies/companies.state';
import { GetJob, InitStaticData as InitJobsStaticData } from '@store/jobs/jobs.actions';
import { JobsState } from '@store/jobs/jobs.state';
import { CandidateApplicationWebservice } from '@webservices/candidate-api/candidate-application.webservice';
import { WINDOW } from '@wizbii/angular-utilities';
import { ScrollToService } from '@wizbii/nicky-lenaers_ngx-scroll-to';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, finalize, first, map, shareReplay, switchMap, tap } from 'rxjs/operators';

// tslint:disable:relative-url-prefix
@Component({
  templateUrl: './apply.component.html',
  styleUrls: ['./apply.component.scss', '../job/job.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [skeletonEnter(), listAnimation, staggerAnimation()],
})
export class ApplyComponent implements OnInit, AfterViewInit, OnDestroy {
  // tslint:enable:relative-url-prefix
  @Select(CompaniesState.authenticatedCompany)
  company$: Observable<Company>;

  job$: Observable<Job>;

  durationEnum = DurationEnum;

  send = false;
  loading = false;
  error: any;

  private readonly destroyed$ = new Subject<void>();

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: Object,
    @Inject(WINDOW) private readonly window: Window,
    private readonly store: Store,
    readonly activatedRoute: ActivatedRoute,
    private readonly candidateApplicationWebservice: CandidateApplicationWebservice,
    private readonly cdr: ChangeDetectorRef,
    private readonly scrollToService: ScrollToService,
    private readonly eventTrackerService: EventTrackerService
  ) {
    this.job$ = activatedRoute.paramMap.pipe(
      map(paramMap => paramMap.get('jobId')),
      distinctUntilChanged(),
      tap(jobId => store.dispatch(new GetJob(jobId))),
      switchMap(jobId => store.select(JobsState.job(jobId))),
      shareReplay()
    );
  }

  ngOnInit(): void {
    this.store.dispatch(new InitJobsStaticData());
    this.store.dispatch(new InitApplicationStaticData());
  }

  ngAfterViewInit(): void {
    this.scrollToForm();
  }

  onSubmit({ form, jobId }: { form: FormGroup; jobId: string }) {
    if (form.invalid) {
      this.eventTrackerService.send({
        eventCategory: 'Home PreLaunch',
        eventAction: 'Form Status',
        eventLabel: [
          !form.controls.firstname.valid ? 'firstname' : undefined,
          !form.controls.lastname.valid ? 'lastname' : undefined,
          !form.controls.email.valid ? 'email' : undefined,
          !form.controls.phone.valid ? 'phone' : undefined,
          !form.controls.presentation.valid ? 'presentation' : undefined,
          !form.controls.cv.valid ? 'cv' : undefined,
        ]
          .filter(error => !!error)
          .join(','),
      });

      return;
    }

    this.loading = true;
    this.error = null;

    this.candidateApplicationWebservice
      .create(form, jobId)
      .pipe(
        first(),
        catchError(error => {
          this.error = error;
          this.cdr.markForCheck();
          return throwError(error);
        }),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe(() => {
        this.send = true;
        this.cdr.markForCheck();
      });
  }

  private scrollToForm(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.window.setTimeout(() => {
      this.scrollToService.scrollTo({
        target: '#application-form',
        offset: 20,
      });
    }, 500);
  }

  trackByLabel(_, skill: string) {
    return skill;
  }

  ngOnDestroy(): void {
    this.store.dispatch(new SetTmpApplication(null));

    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
